import { useCallback, useEffect, useRef, useState } from "react";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { useTranslation } from "react-i18next";
import type { Channel as StreamChannelType } from "stream-chat";
import { useChatContext } from "stream-chat-react";
import {
  Building2,
  LogOut,
  Plus,
  Search,
  Settings,
  Trash2,
  UserMinus,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useStreamChat } from "@/features/chat/context/StreamChatContext";
import { UserAvatar } from "@/features/chat/components/UserAvatar";
import { streamService } from "@/features/chat/services/streamService";
import { PastRecordings } from "@/features/call/components/PastRecordings";

interface Props {
  channel: StreamChannelType;
  onClose: () => void;
}

export const ChannelSettingsModal = ({ channel, onClose }: Props) => {
  const { t } = useTranslation();
  const { client: chatClient, setActiveChannel } = useChatContext();
  const { videoClient } = useStreamChat();

  const [newName, setNewName] = useState(((channel.data as any)?.name as string) || "");
  const [members, setMembers] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  // Past call IDs for this channel (fetched from Stream Video queryCalls)
  const [pastCallIds, setPastCallIds] = useState<string[]>([]);
  const [callsLoading, setCallsLoading] = useState(false);

  // Add member search state
  const [showAddSearch, setShowAddSearch] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  const [addSearchCompany, setAddSearchCompany] = useState("");
  const [addSearchBatch, setAddSearchBatch] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const addSearchRequestRef = useRef(0);

  const debouncedAddSearch = useDebounce(addSearch, 300);
  const debouncedAddSearchCompany = useDebounce(addSearchCompany, 300);
  const debouncedAddSearchBatch = useDebounce(addSearchBatch, 300);

  const isOfficer = useCallback((user: any) => {
    return ((user?.user_type || "").toLowerCase()) === "officer";
  }, []);

  const canEdit = useCallback(() => {
    if (!chatClient?.user) return false;
    const createdById = (channel.data as any)?.created_by?.id;
    if (createdById === chatClient.userID) return true;
    if (isOfficer(chatClient.user)) return true;
    return false;
  }, [channel, chatClient, isOfficer]);

  useEffect(() => {
    const loadMembers = async () => {
      const state = await channel.watch();
      setMembers(Object.values(state.members));
    };
    loadMembers();
  }, [channel]);

  // Fetch past call IDs for this channel from the Stream Video API.
  // Group calls store channelId in call custom data so we can filter on it.
  useEffect(() => {
    if (!videoClient || !channel.id) return;
    setCallsLoading(true);
    videoClient
      .queryCalls({
        filter_conditions: { "custom.channelId": channel.id } as any,
        sort: [{ field: "created_at", direction: -1 }],
        limit: 20,
      })
      .then((res) => {
        setPastCallIds(res.calls.map((c) => c.id));
      })
      .catch((err) => console.error("[ChannelSettings] queryCalls failed:", err))
      .finally(() => setCallsLoading(false));
  }, [videoClient, channel.id]);

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await channel.updatePartial({ set: { name: newName } as any });
      alert(t("chat.group_name_updated"));
    } catch {
      alert(t("chat.could_not_update_name"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t("chat.delete_group_confirm"))) return;
    try {
      setActiveChannel(undefined);
      await channel.delete();
      onClose();
    } catch {
      alert(t("chat.could_not_delete_group"));
    }
  };

  const handleRemoveMember = async (userId: string, userName: string) => {
    if (userId === chatClient?.userID) return;
    if (!window.confirm(t("chat.remove_member_confirm", { name: userName }))) return;
    try {
      const actorName = (chatClient?.user as any)?.name || chatClient?.userID || "";
      await channel.sendMessage({
        text: `${actorName} removed ${userName}`,
        hp_system_event: "member_removed",
        hp_actor_name: actorName,
        hp_member_name: userName,
      } as any);
      await streamService.removeChannelMember(channel.id!, userId);
      setMembers((prev) => prev.filter((m) => m.user.id !== userId));
    } catch {
      alert(t("chat.could_not_remove_member"));
    }
  };

  const handleLeaveGroup = async () => {
    if (!chatClient?.userID) return;
    if (!window.confirm(t("chat.leave_group_confirm"))) return;
    try {
      const myName = (chatClient.user as any)?.name || chatClient.userID;
      await channel.sendMessage({
        text: `${myName} left the group`,
        hp_system_event: "member_left",
        hp_member_name: myName,
      } as any);
      await streamService.removeChannelMember(channel.id!, chatClient.userID);
      setActiveChannel(undefined);
      onClose();
    } catch {
      alert(t("chat.could_not_leave_group"));
    }
  };

  const searchUsersToAdd = useCallback(async (
    nameQuery: string,
    companyQuery: string,
    batchQuery: string
  ) => {
    if (!chatClient) return;
    const requestId = ++addSearchRequestRef.current;
    if (!nameQuery.trim() && !companyQuery.trim() && !batchQuery.trim()) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    try {
      let filtered = (await streamService.getContacts(nameQuery)).filter((u) => u.id !== chatClient.userID);

      if (companyQuery.trim()) {
        const q = companyQuery.trim().toLowerCase();
        filtered = filtered.filter((u: any) => {
          const cn = (u.company_name || u.company || "").toLowerCase();
          return cn.includes(q);
        });
      }

      if (companyQuery.trim() && batchQuery.trim()) {
        const bq = batchQuery.trim().toLowerCase();
        filtered = filtered.filter((u: any) => {
          const bn = (u.batch_no || "").toLowerCase();
          return bn.includes(bq);
        });
      }

      if (requestId !== addSearchRequestRef.current) return;
      const memberIds = members.map((m) => m.user.id);
      setSearchResults(filtered.filter((u) => !memberIds.includes(u.id)));
    } catch (error) {
      console.error("Error searching users:", error);
    } finally {
      if (requestId === addSearchRequestRef.current) setSearchLoading(false);
    }
  }, [chatClient, members]);

  useEffect(() => {
    searchUsersToAdd(debouncedAddSearch, debouncedAddSearchCompany, debouncedAddSearchBatch);
  }, [searchUsersToAdd, debouncedAddSearch, debouncedAddSearchCompany, debouncedAddSearchBatch]);

  const handleAddMember = async (user: any) => {
    try {
      const channelId = channel.id!;
      await streamService.addChannelMember(channelId, user.id);
      const state = await channel.watch();
      setMembers(Object.values(state.members));
      setSearchResults((prev) => prev.filter((u) => u.id !== user.id));
      const actorName = (chatClient?.user as any)?.name || chatClient?.userID || "";
      const memberName = user.name || user.id;
      await channel.sendMessage({
        text: `${actorName} added ${memberName}`,
        hp_system_event: "member_added",
        hp_actor_name: actorName,
        hp_member_name: memberName,
      } as any);
      alert(t("chat.member_added", { name: memberName }));
    } catch {
      alert(t("chat.could_not_add_member"));
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-950/45 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="glass bg-white w-full sm:w-[540px] sm:max-h-[84vh] max-h-[92vh] rounded-t-2xl sm:rounded-2xl flex flex-col shadow-2xl overflow-hidden border border-white/70">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-slate-200/80 bg-white/80 flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
            <Settings className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-slate-900 leading-tight">{t("chat.group_settings")}</h2>
            <p className="text-xs text-slate-400 mt-0.5">{t("chat.members_count", { count: members.length })}</p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-slate-100 active:bg-slate-200 flex items-center justify-center transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-4.5 h-4.5 text-slate-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto hp-channel-scroll bg-white/60">
          {/* Group Name */}
          <div className="px-4 py-4 border-b border-slate-200/80 bg-white/70">
            {canEdit() ? (
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2 block">{t("chat.group_name")}</label>
                <div className="flex gap-2">
                  <div className="flex items-center gap-2 bg-slate-100/80 border border-transparent rounded-xl px-3 h-11 transition-shadow focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:bg-white focus-within:border-indigo-100 flex-1 min-w-0">
                    <Users className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="flex-1 min-w-0 bg-transparent outline-none text-sm font-medium text-slate-900 placeholder:text-slate-400"
                    />
                  </div>
                  <button
                    onClick={handleUpdate}
                    disabled={saving}
                    className="h-11 px-4 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 transition-colors flex-shrink-0"
                  >
                    {t("common.save")}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1 block">{t("chat.group_name")}</label>
                <p className="text-base font-semibold text-slate-900">{newName || t("chat.unnamed_group")}</p>
              </div>
            )}
          </div>

          {/* Members Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200/80 bg-white/70">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{t("chat.members_count", { count: members.length })}</span>
            {canEdit() && (
              <button
                onClick={() => {
                  setShowAddSearch(!showAddSearch);
                  setAddSearch("");
                  setAddSearchCompany("");
                  setAddSearchBatch("");
                  setSearchResults([]);
                }}
                className="flex items-center gap-1.5 px-3 h-9 bg-indigo-50 text-indigo-700 text-sm font-medium rounded-lg hover:bg-indigo-100 active:bg-indigo-200 transition-colors"
              >
                <UserPlus className="w-4 h-4" />
                {t("chat.add")}
              </button>
            )}
          </div>

          {/* Add Member Search */}
          {showAddSearch && (
            <div className="px-4 py-3 bg-slate-50/80 border-b border-slate-200/80 space-y-2">
              <div className="flex items-center gap-2 bg-white rounded-xl px-3 h-10 border border-slate-200 transition-shadow focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-100">
                <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <input
                  type="text"
                  placeholder={t("chat.search_users")}
                  value={addSearch}
                  onChange={(e) => {
                    setAddSearch(e.target.value);
                  }}
                  className="flex-1 min-w-0 bg-transparent outline-none text-sm text-slate-900 placeholder:text-slate-400"
                  autoFocus
                />
              </div>
              <div className="flex items-center gap-2 bg-white rounded-xl px-3 h-10 border border-slate-200 transition-shadow focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-100">
                <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <input
                  type="text"
                  placeholder={t("chat.search_company")}
                  value={addSearchCompany}
                  onChange={(e) => {
                    setAddSearchCompany(e.target.value);
                    if (!e.target.value.trim()) {
                      setAddSearchBatch("");
                    }
                  }}
                  className="flex-1 min-w-0 bg-transparent outline-none text-sm text-slate-900 placeholder:text-slate-400"
                />
              </div>

              {addSearchCompany.trim() && (
                <div className="flex items-center gap-2 bg-white rounded-xl px-3 h-10 border border-slate-200 transition-shadow focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-100">
                  <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <input
                    type="text"
                    placeholder={t("chat.search_batch")}
                    value={addSearchBatch}
                    onChange={(e) => {
                      setAddSearchBatch(e.target.value);
                    }}
                    className="flex-1 min-w-0 bg-transparent outline-none text-sm text-slate-900 placeholder:text-slate-400"
                  />
                </div>
              )}

              {/* Search results */}
              {searchLoading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600" />
                </div>
              ) : (
                <div className="max-h-44 overflow-y-auto hp-channel-scroll">
                  {searchResults.map((user) => {
                    const userIsOfficer = isOfficer(user);
                    const displayCompany = user.company_name || user.company || "";
                    const batchNo = user.batch_no;

                    return (
                      <div
                        key={user.id}
                        className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-b-0"
                      >
                        <UserAvatar name={user.name} id={user.id} image={user.image} size={36} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">{user.name || user.id}</p>
                          {!userIsOfficer && displayCompany && (
                            <p className="text-xs text-slate-500 truncate">
                              {displayCompany}
                              {batchNo && ` · ${t("profile.batch_no")} ${batchNo}`}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => handleAddMember(user)}
                          className="w-8 h-8 text-indigo-600 hover:bg-indigo-50 active:bg-indigo-100 rounded-full flex items-center justify-center transition-colors"
                          aria-label={`Add ${user.name || user.id}`}
                        >
                          <Plus className="w-5 h-5" />
                        </button>
                      </div>
                    );
                  })}
                  {(addSearch.trim() || addSearchCompany.trim()) &&
                    !searchLoading &&
                    searchResults.length === 0 && (
                      <p className="text-center text-sm text-slate-400 py-3">{t("chat.no_users_found")}</p>
                    )}
                </div>
              )}
            </div>
          )}

          {/* Member list */}
          <div>
            {members.map((m) => {
              const user = m.user;
              const userIsOfficer = isOfficer(user);
              const displayCompany = user?.company_name || user?.company || "";
              const batchNo = user?.batch_no;
              const visaType = user?.visa_type_descr || "";
              const isCreator = (channel.data as any)?.created_by?.id === user.id;
              const isMe = user.id === chatClient?.userID;

              return (
                <div key={user.id} className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
                  <UserAvatar name={user.name} id={user.id} image={user.image} size={40} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-slate-900 truncate">{user.name || user.id}</p>
                      {isCreator && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded font-medium">
                          {t("chat.creator")}
                        </span>
                      )}
                      {isMe && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded font-medium">
                          {t("chat.you")}
                        </span>
                      )}
                    </div>
                    {!userIsOfficer && displayCompany && (
                      <p className="text-xs text-slate-500 truncate" title={[displayCompany, batchNo ? `${t("profile.batch_no")} ${batchNo}` : "", visaType].filter(Boolean).join(" · ")}>
                        {displayCompany}
                        {batchNo && ` · ${t("profile.batch_no")} ${batchNo}`}
                        {visaType && ` · ${visaType}`}
                      </p>
                    )}
                  </div>
                  {canEdit() && !isMe && (
                    <button
                      onClick={() => handleRemoveMember(user.id, user.name || user.id)}
                      className="w-8 h-8 text-rose-500 hover:bg-rose-50 active:bg-rose-100 rounded-full flex items-center justify-center transition-colors"
                      aria-label={`Remove ${user.name || user.id}`}
                    >
                      <UserMinus className="w-5 h-5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Past Recordings */}
          <div className="px-4 py-4 border-b border-slate-200/80 bg-white/70">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">{t("chat.past_recordings")}</p>
            {callsLoading ? (
              <div className="flex items-center gap-2 text-slate-400 text-sm py-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-400" />
                <span>{t("common.loading")}</span>
              </div>
            ) : pastCallIds.length === 0 ? (
              <p className="text-sm text-slate-400">{t("chat.no_recordings_available")}</p>
            ) : (
              <div className="space-y-2">
                {pastCallIds.map((callId) => (
                  <PastRecordings key={callId} callId={callId} />
                ))}
              </div>
            )}
          </div>

          {/* Leave group */}
          <div className="px-4 py-3 bg-white/70">
            <button
              onClick={handleLeaveGroup}
              className="flex items-center gap-3 text-orange-600 hover:bg-orange-50 active:bg-orange-100 w-full h-11 px-3 rounded-xl transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-semibold text-sm">{t("chat.leave_group")}</span>
            </button>
          </div>

          {/* Delete group */}
          {canEdit() && (
            <div className="px-4 pb-4 bg-white/70">
              <button
                onClick={handleDelete}
                className="flex items-center gap-3 text-rose-600 hover:bg-rose-50 active:bg-rose-100 w-full h-11 px-3 rounded-xl transition-colors"
              >
                <Trash2 className="w-5 h-5" />
                <span className="font-semibold text-sm">{t("chat.delete_group")}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
